import { FC, useEffect, useMemo, useState } from "react";

import {
  ArrowRightIcon,
  Box,
  Button,
  ButtonGroup,
  CheckIcon,
  CloseIcon,
  Column,
  Dialog,
  Row,
  Text,
} from "@hightouch/htui";
import { Editor } from "src/components/editor";
import { v10Engine } from "src/utils/liquid-v10-engine";

import { preprocessTraitEnrichments } from "./liquid-trait-preprocess";
import {
  NoSampleData,
  PreviewHeaderCell,
  PreviewTableFrame,
  SampleDataHeader,
  SampleDataLoading,
  SampleDataTable,
} from "./template-preview-table";

const MAX_SAMPLE_ROWS = 5;

type TemplateField = {
  to: string;
  template: string;
};

type EvalResult = {
  output: string | null;
  error: string | null;
};

type Props = {
  onClose: () => void;
  onApply: (newTemplate: string) => void;
  rows: Record<string, unknown>[] | undefined;
  loadingRows?: boolean;
  loadingRowKeys?: boolean;
  onLoadRows?: () => void;
  templateFields: TemplateField[];
  modelName?: string;
  sourceIcon?: string;
  sourceName?: string;
  // Maps trait references to their corresponding hashed row keys.
  traitRowKeys?: Record<string, string>;
};

function evaluateTemplate(
  template: string,
  row: Record<string, unknown>,
  traitRowKeys: Record<string, string>,
): EvalResult {
  try {
    const preprocessedTemplate = preprocessTraitEnrichments(
      template,
      traitRowKeys,
    );
    const output = v10Engine.parseAndRenderSync(preprocessedTemplate, { row });
    return { output: String(output), error: null };
  } catch (e) {
    return { output: null, error: (e as Error).message };
  }
}

export const TemplatePreviewModal: FC<Props> = ({
  onClose,
  onApply,
  rows,
  loadingRows,
  loadingRowKeys,
  onLoadRows,
  templateFields,
  modelName,
  sourceIcon,
  sourceName,
  traitRowKeys = {},
}) => {
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!rows?.length && !loadingRows && onLoadRows) {
      onLoadRows();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = Boolean(loadingRows || loadingRowKeys);
  const field = templateFields[0];
  const [template, setTemplate] = useState(field?.template ?? "");
  const [results, setResults] = useState<EvalResult[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [v10Ok, setV10Ok] = useState<boolean | null>(null);

  const sampleRows = useMemo(
    () => (rows ?? []).slice(0, MAX_SAMPLE_ROWS),
    [rows],
  );
  const columns = useMemo(() => {
    if (sampleRows.length === 0) return [];
    return Object.keys(sampleRows[0]!);
  }, [sampleRows]);

  const evaluate = () => {
    if (!template.includes("{")) {
      setV10Ok(true);
      setParseError(null);
      setResults(sampleRows.map(() => ({ output: template, error: null })));
      return;
    }

    try {
      v10Engine.parse(template);
      setParseError(null);
      setV10Ok(true);
    } catch (e) {
      setParseError((e as Error).message);
      setV10Ok(false);
      setResults(null);
      return;
    }

    const evalResults: EvalResult[] = [];
    for (const row of sampleRows) {
      evalResults.push(evaluateTemplate(template, row, traitRowKeys));
    }
    setResults(evalResults);
  };

  const handleApply = () => {
    if (field) {
      onApply(template);
    }
    onClose();
  };

  if (!field) return null;

  return (
    <Dialog
      isOpen
      variant="form"
      title="Liquid template tester"
      width="4xl"
      actions={
        <Row justify="space-between" width="100%" align="center">
          <Row gap={2} align="center">
            {v10Ok !== null && (
              <Row
                align="center"
                gap={1.5}
                px={2}
                py={1}
                borderRadius="md"
                bg={v10Ok ? "success.background" : "danger.background"}
              >
                <Box
                  as={v10Ok ? CheckIcon : CloseIcon}
                  boxSize={3.5}
                  color={v10Ok ? "success.base" : "danger.base"}
                />
                <Text
                  size="sm"
                  fontWeight="medium"
                  color={v10Ok ? "success.base" : "danger.base"}
                >
                  Liquid v10+ compatible
                </Text>
              </Row>
            )}
          </Row>
          <ButtonGroup>
            <Button onClick={evaluate}>Evaluate</Button>
            <Button variant="primary" onClick={handleApply}>
              Apply to template
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </ButtonGroup>
        </Row>
      }
      onClose={onClose}
    >
      <Column gap={4}>
        <Column gap={1}>
          <Text fontWeight="medium" size="sm" color="text.secondary">
            Template
          </Text>
          <Editor
            language="liquid"
            minHeight="200px"
            value={template}
            onChange={(value) => {
              setTemplate(value);
              setV10Ok(null);
              setResults(null);
              setParseError(null);
            }}
          />
          {parseError && (
            <Box
              px={3}
              py={2}
              bg="danger.background"
              borderRadius="md"
              border="1px solid"
              borderColor="danger.border"
            >
              <Box fontFamily="mono" fontSize="sm" color="danger.base">
                {parseError}
              </Box>
            </Box>
          )}
        </Column>

        {loading ? (
          <SampleDataLoading />
        ) : sampleRows.length > 0 ? (
          <Column gap={2}>
            <SampleDataHeader
              count={sampleRows.length}
              modelName={modelName}
              sourceIcon={sourceIcon}
              sourceName={sourceName}
            />

            <Row align="center">
              <Box flex={1} minWidth={0}>
                <SampleDataTable rows={sampleRows} columns={columns} />
              </Box>

              {results && (
                <>
                  <Box flexShrink={0} px={3}>
                    <Box
                      as={ArrowRightIcon}
                      boxSize={5}
                      color="text.tertiary"
                    />
                  </Box>

                  <PreviewTableFrame scrollX={false}>
                    <Box as="thead">
                      <Box as="tr" bg="base.background">
                        <PreviewHeaderCell>Result</PreviewHeaderCell>
                      </Box>
                    </Box>
                    <Box as="tbody">
                      {results.map((result, i) => (
                        <Box
                          as="tr"
                          key={i}
                          borderTop="1px solid"
                          borderColor="base.border"
                        >
                          <Box
                            as="td"
                            px={2}
                            py={1}
                            fontSize="xs"
                            fontWeight="medium"
                            color={
                              result.error ? "danger.base" : "text.primary"
                            }
                            whiteSpace="nowrap"
                          >
                            {result.output ?? result.error}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </PreviewTableFrame>
                </>
              )}
            </Row>
          </Column>
        ) : (
          <NoSampleData onLoadRows={onLoadRows} />
        )}
      </Column>
    </Dialog>
  );
};
